import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Book, ChevronDown, ChevronRight, Users, Calendar, Trophy, ClipboardCheck, BarChart3, Shield, MessageCircle, HelpCircle, Star, UserCheck, Baby, FileText, Database, Pencil, Trash2, Plus, Save, X, GripVertical, Download } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders, isFullAdmin } from '../lib/auth';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_MAP = {
  Book, Shield, Baby, Calendar, Trophy, UserCheck, ClipboardCheck, Users, BarChart3, MessageCircle, Database, Star, HelpCircle, FileText,
};

const ManualAjuda = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState(null);
  const admin = isFullAdmin();

  const fetchSections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/help/sections`);
      setSections(res.data.sections || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sections;
    const term = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return sections.filter(s => {
      const inTitle = s.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term);
      const inKeywords = (s.keywords || []).some(k => k.normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term));
      const inContent = (s.content || []).some(c =>
        c.subtitle.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term) ||
        c.text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term)
      );
      return inTitle || inKeywords || inContent;
    });
  }, [searchTerm, sections]);

  const toggleSection = (id) => setExpandedSection(prev => prev === id ? null : id);

  const startEdit = (section) => {
    setEditingSection(section.id);
    setEditData({
      title: section.title,
      icon: section.icon || 'HelpCircle',
      color: section.color || 'text-gray-700',
      keywords: (section.keywords || []).join(', '),
      content: (section.content || []).map(c => ({ ...c })),
      order: section.order || 0,
    });
  };

  const cancelEdit = () => { setEditingSection(null); setEditData(null); };

  const saveEdit = async (sectionId) => {
    try {
      const payload = {
        title: editData.title,
        icon: editData.icon,
        color: editData.color,
        keywords: editData.keywords.split(',').map(k => k.trim()).filter(Boolean),
        content: editData.content,
        order: editData.order,
      };
      await axios.put(`${API}/help/sections/${sectionId}`, payload, { headers: getAdminHeaders() });
      toast.success('Secao atualizada!');
      cancelEdit();
      fetchSections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const deleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/help/sections/${sectionId}`, { headers: getAdminHeaders() });
      toast.success('Secao removida');
      fetchSections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  const addNewSection = async () => {
    try {
      const newSection = {
        title: 'Nova Secao',
        icon: 'HelpCircle',
        color: 'text-gray-700',
        keywords: [],
        content: [{ subtitle: 'Subtitulo', text: 'Conteudo aqui...' }],
        order: sections.length + 1,
      };
      await axios.post(`${API}/help/sections`, newSection, { headers: getAdminHeaders() });
      toast.success('Secao criada!');
      fetchSections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar'); }
  };

  const updateContentItem = (idx, field, value) => {
    setEditData(prev => {
      const content = [...prev.content];
      content[idx] = { ...content[idx], [field]: value };
      return { ...prev, content };
    });
  };

  const addContentItem = () => {
    setEditData(prev => ({
      ...prev,
      content: [...prev.content, { subtitle: '', text: '' }]
    }));
  };

  const removeContentItem = (idx) => {
    setEditData(prev => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== idx)
    }));
  };

  const renderText = (text) => {
    return text.split('\n').map((line, i) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const rendered = parts.map((part, j) =>
        j % 2 === 1 ? <strong key={j} className="font-bold text-gray-900">{part}</strong> : part
      );
      if (line.trim().startsWith('- ')) {
        return <li key={i} className="ml-4 list-disc text-gray-600">{rendered}</li>;
      }
      return <p key={i} className={`text-gray-600 ${i > 0 ? 'mt-1' : ''}`}>{rendered}</p>;
    });
  };

  const getIcon = (iconName) => ICON_MAP[iconName] || HelpCircle;

  if (loading) return (
    <div className="min-h-screen bg-[#FAFCFB] flex items-center justify-center">
      <p className="font-inter text-gray-500">Carregando...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFCFB] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-4 pt-6 pb-8">
        <button onClick={() => navigate(-1)} className="mb-3 hover:opacity-80" data-testid="btn-back">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <HelpCircle className="w-8 h-8" />
          <h1 className="font-nunito text-2xl font-bold">Manual do Usuario</h1>
        </div>
        <p className="font-inter text-sm text-white/70">Tire suas duvidas sobre o funcionamento do Projeto Semente</p>

        {/* Download PDF */}
        <a href={`${API}/manual/portal-pais/pdf`} target="_blank" rel="noopener noreferrer"
           data-testid="btn-download-manual-pdf"
           className="inline-flex items-center gap-2 mt-3 bg-[#F4D35E] text-[#1A4D2E] px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#e5c64e] transition-all">
          <Download className="w-4 h-4" /> Manual do Portal da Familia (PDF)
        </a>

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
          <input
            type="text"
            data-testid="search-help"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por palavra-chave... Ex: escala, ranking, backup"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white text-gray-800 text-sm font-inter placeholder:text-gray-400 border-0 focus:ring-2 focus:ring-[#1A4D2E]/30"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {/* Admin action bar */}
        {admin && !searchTerm && (
          <div className="flex justify-end">
            <Button
              data-testid="btn-add-section"
              onClick={addNewSection}
              className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-4 h-9 text-sm"
            >
              <Plus className="w-4 h-4 mr-1" /> Nova Secao
            </Button>
          </div>
        )}

        {/* Table of Contents */}
        {!searchTerm && !expandedSection && (
          <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
            <h2 className="font-nunito font-bold text-[#1A4D2E] text-lg mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Sumario
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {sections.map((s, i) => {
                const Icon = getIcon(s.icon);
                return (
                  <button
                    key={s.id}
                    data-testid={`toc-${s.id}`}
                    onClick={() => setExpandedSection(s.id)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F2F7F4] transition-colors text-left group"
                  >
                    <span className="text-xs font-inter font-bold text-gray-300 w-5">{String(i + 1).padStart(2, '0')}</span>
                    <Icon className={`w-4 h-4 ${s.color || 'text-gray-600'}`} />
                    <span className="font-inter text-sm font-medium text-gray-700 group-hover:text-[#1A4D2E] transition-colors">{s.title}</span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Sections */}
        {filteredSections.length === 0 ? (
          <Card className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-inter text-gray-500">Nenhum resultado encontrado para "<strong>{searchTerm}</strong>"</p>
            <p className="font-inter text-xs text-gray-400 mt-1">Tente outras palavras-chave</p>
          </Card>
        ) : (
          filteredSections.map(section => {
            const isExpanded = expandedSection === section.id || !!searchTerm;
            const isEditing = editingSection === section.id;
            const Icon = getIcon(section.icon);

            return (
              <Card key={section.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Section Header */}
                <div className="flex items-center">
                  <button
                    data-testid={`section-${section.id}`}
                    onClick={() => toggleSection(section.id)}
                    className="flex-1 flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 transition-colors text-left"
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${section.color || 'text-gray-600'}`} />
                    <span className="font-nunito font-bold text-gray-800 flex-1">{section.title}</span>
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                  </button>
                  {admin && !isEditing && (
                    <div className="flex gap-1 pr-3">
                      <button
                        data-testid={`edit-section-${section.id}`}
                        onClick={() => { if (!isExpanded) setExpandedSection(section.id); startEdit(section); }}
                        className="p-2 text-gray-400 hover:text-[#1A4D2E] rounded-lg hover:bg-gray-100 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            data-testid={`delete-section-${section.id}`}
                            className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir secao</AlertDialogTitle>
                            <AlertDialogDescription>Tem certeza que deseja excluir a secao <strong>{section.title}</strong>?</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteSection(section.id)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>

                {/* Edit Mode */}
                {isEditing && editData && (
                  <div className="px-5 pb-5 border-t border-amber-200 bg-amber-50/30 pt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="font-inter text-xs font-semibold text-gray-600 mb-1 block">Titulo</label>
                        <input
                          data-testid="edit-title"
                          value={editData.title}
                          onChange={e => setEditData(p => ({ ...p, title: e.target.value }))}
                          className="w-full rounded-lg border border-gray-200 h-9 px-3 text-sm"
                        />
                      </div>
                      <div>
                        <label className="font-inter text-xs font-semibold text-gray-600 mb-1 block">Palavras-chave (virgula)</label>
                        <input
                          data-testid="edit-keywords"
                          value={editData.keywords}
                          onChange={e => setEditData(p => ({ ...p, keywords: e.target.value }))}
                          className="w-full rounded-lg border border-gray-200 h-9 px-3 text-sm"
                          placeholder="Ex: escala, trabalho, servir"
                        />
                      </div>
                    </div>

                    {/* Content Items */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="font-inter text-xs font-semibold text-gray-600">Conteudo</label>
                        <Button
                          data-testid="btn-add-content-item"
                          onClick={addContentItem}
                          variant="outline"
                          className="h-7 px-3 text-xs rounded-full border-[#1A4D2E] text-[#1A4D2E]"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Sub-topico
                        </Button>
                      </div>
                      {editData.content.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl border border-gray-200 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                            <input
                              data-testid={`edit-subtitle-${idx}`}
                              value={item.subtitle}
                              onChange={e => updateContentItem(idx, 'subtitle', e.target.value)}
                              className="flex-1 rounded-lg border border-gray-200 h-8 px-2 text-sm font-semibold"
                              placeholder="Subtitulo"
                            />
                            {editData.content.length > 1 && (
                              <button
                                onClick={() => removeContentItem(idx)}
                                className="p-1 text-gray-400 hover:text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <textarea
                            data-testid={`edit-text-${idx}`}
                            value={item.text}
                            onChange={e => updateContentItem(idx, 'text', e.target.value)}
                            rows={4}
                            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm resize-y"
                            placeholder="Use **negrito**, - para listas, \n para quebra de linha"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Save/Cancel */}
                    <div className="flex gap-2 justify-end pt-2">
                      <Button
                        data-testid="btn-cancel-edit"
                        onClick={cancelEdit}
                        variant="outline"
                        className="rounded-full h-9 px-4 text-sm"
                      >
                        <X className="w-4 h-4 mr-1" /> Cancelar
                      </Button>
                      <Button
                        data-testid="btn-save-edit"
                        onClick={() => saveEdit(section.id)}
                        className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full h-9 px-4 text-sm"
                      >
                        <Save className="w-4 h-4 mr-1" /> Salvar
                      </Button>
                    </div>
                  </div>
                )}

                {/* View Mode */}
                {isExpanded && !isEditing && (
                  <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                    {(section.content || []).map((item, idx) => (
                      <div key={idx}>
                        <h4 className="font-inter font-semibold text-gray-800 text-sm mb-1.5">{item.subtitle}</h4>
                        <div className="font-inter text-sm leading-relaxed">
                          {renderText(item.text)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })
        )}

        {/* Footer */}
        <div className="text-center pt-4">
          <p className="font-inter text-xs text-gray-400">Projeto Semente - Ministério Raízes</p>
        </div>
      </div>

      <Navigation active="ajuda" />
    </div>
  );
};

export default ManualAjuda;
